import {
  Box,
  Button,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridColDef, GridRowsProp, GridValidRowModel } from "@mui/x-data-grid";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Company,
  CompanyType,
  Resource,
  Scope,
} from "../../../../types/NendaTypes";
import { getCompanyIdByPremise } from "../../../utils/company";
import ConfirmationDialog from "../ConfirmationDialog";
import CreateEditCompanyForm from "../components/Dashboard/company/CreateEditCompanyForm";
import PermissionsGate from "../components/PermissionGate";
import { CustomerPortalState, store } from "../store";
import { DeleteCompany, getCompanies } from "../store/reducers/companyReducer";
import DefaultDataGrid, {
  RowOption,
} from "../ui-components/dataGrid/DefaultDataGrid";

export const CompanyTypeLabel = new Map<CompanyType, string>([
  [CompanyType.CUSTOMER, "Customers"],
  [CompanyType.RESELLER, "Resellers"],
  [CompanyType.SYSTEM_INTEGRATOR, "System Integrators"],
  [CompanyType.ADVERTISER, "Advertiser"],
]);

const Companies = () => {
  const theme = useTheme();
  const naviate = useNavigate();
  const companyType = CompanyType.CUSTOMER;
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [companyIdToDelete, setcCompanyIdToDelete] = useState<
    string | undefined
  >();

  const createStatus = useSelector(
    (state: CustomerPortalState) => state.company.status.createCompany
  );

  const allPremises = useSelector(
    (state: CustomerPortalState) => state.organizationUnits.premises.data
  );
  const allUsers = useSelector(
    (state: CustomerPortalState) => state.user.users
  );
  const companyList = useSelector(getCompanies);

  useEffect(() => {
    if (createStatus === "succeeded") setIsFormVisible(false);
  }, [createStatus]);

  const toggleForm = () => setIsFormVisible(!isFormVisible);

  const handleDeleteCompany = (companyId: string) => {
    setcCompanyIdToDelete(companyId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteCompany = (companyId: string) => {
    store.dispatch(DeleteCompany(companyId));
  };

  const rows: GridRowsProp = companyList.map(
    (company: Company & { created?: string }) => {
      return {
        id: company._id,
        name: company.name,
        type: company.type,
        created_at: company.createdAt || company.created,
        premises: allPremises.filter((premise) => {
          const companyId = getCompanyIdByPremise(premise);
          return companyId === company._id;
        }).length,
        users: allUsers.data.filter((user) => user.company === company._id)
          .length,
      };
    }
  );

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("customerportal.pages.companies.name") ?? "Name",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        return (
          <Link
            sx={{ color: theme.palette.primary.dark, cursor: "pointer" }}
            onClick={() => naviate(`/dashboard/company/${params.row.id}`)}
          >
            {params.value}
          </Link>
        );
      },
    },
    {
      field: "type",
      headerName:
        t("customerportal.pages.companies.customer_type") ?? "Customer type",
      flex: 1,
      sortable: true,
    },
    {
      field: "premises",
      headerName:
        t("customerportal.pages.companies.no_of_premises") ?? "No of Premises",
      flex: 1,
      sortable: true,
    },
    {
      field: "users",
      headerName:
        t("customerportal.pages.companies.no_of_users") ?? "No of Users",
      flex: 1,
      sortable: true,
    },
    {
      field: "created_at",
      headerName: t("customerportal.pages.companies.created") ?? "Created at",
      flex: 1,
      sortable: true,
      valueFormatter: (params) => {
        return new Date(params as string).toLocaleDateString();
      },
    },
  ];

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const rowOptions = (row: GridValidRowModel): RowOption[] => [
    {
      label: t("customerportal.pages.companies.delete"),
      value: "delete",
      onClick: () => handleDeleteCompany(row.id as string),
    },
  ];

  return (
    <PermissionsGate
      restriction={{
        resource: Resource.Company,
        scopes: [Scope.CanCreate],
      }}
    >
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: isMobile ? "flex-start" : "center",
            py: "1rem",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              textTransform: "capitalize",
              color: theme.palette.primary.light,
              paddingRight: "1rem",
            }}
          >
            {companyList.length || 0}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              textTransform: "capitalize",
              color: theme.palette.text.primary,
            }}
          >
            {t("customerportal.pages.companies.customers")}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={{ marginLeft: isMobile ? 0 : "2rem" }}
            onClick={toggleForm}
          >
            <Typography variant="button">
              {t("customerportal.pages.companies.create_company")}
            </Typography>
          </Button>
        </Box>

        <CreateEditCompanyForm open={isFormVisible} onClose={toggleForm} />
        <DefaultDataGrid
          columns={columns}
          rows={rows}
          sortingConfig={[{ field: "created_at", sort: "desc" }]}
          visibleColumns={{
            id: false,
            users: isMobile ? false : true,
            type: isMobile ? false : true,
            premises: isMobile ? false : true,
            created_at: isMobile ? false : true,
          }}
          rowOptions={rowOptions}
        />
        <ConfirmationDialog
          title={t(
            "customerportal.pages.companies.confirmation.delete_company"
          )}
          content={t(
            "customerportal.pages.companies.confirmation.cannot_be_restored"
          )}
          isOpen={showDeleteDialog}
          onClose={(isConfirmed: boolean) => {
            if (isConfirmed && companyIdToDelete) {
              confirmDeleteCompany(companyIdToDelete);
            }
            setShowDeleteDialog(false);
            setcCompanyIdToDelete(undefined);
          }}
        ></ConfirmationDialog>
      </Box>
    </PermissionsGate>
  );
};

export default Companies;
